import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch } from "react-redux";
import PageHeader from "../../../../components/common/PageHeader";
import ActionIconButton from "../../../../components/common/ActionIconButton";
import PageLoader from "../../../../components/common/PageLoader";
import ChipList from "../../../../components/common/ChipList";
import TruncatedText from "../../../../components/common/TruncatedText";
import EmptyState from "../../../../components/common/EmptyState";
import SearchFilterBar from "../../../../components/common/SearchFilterBar";
import DataTable from "../../../../components/DataTable";
import { appActions } from "../../../../redux/actions";
import { getApiErrorMessage } from "../../../../helpers";
import { ROLE_SCOPES } from "../../constants";
import { systemRoleService, tenantRoleService } from "../../services";
import useAuth from "../../../../hooks/useAuth";
import {
  collectAllowSuggestions,
  matchesRoleSearch,
  normalizeRole,
  normalizeRoleList,
} from "../../utils/normalizeRole";
import RoleFormDialog from "../RoleFormDialog";
import RoleDetailsDrawer from "../RoleDetailsDrawer";

const RoleManagement = ({
  scope = ROLE_SCOPES.SYSTEM,
  roleService: roleApi,
}) => {
  const dispatch = useDispatch();
  const { activeTenantId, tenantId, accessToken } = useAuth();
  const isSystem = scope === ROLE_SCOPES.SYSTEM;
  const requestTenantId = activeTenantId || tenantId;
  const api = roleApi || (isSystem ? systemRoleService : tenantRoleService);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [appliedName, setAppliedName] = useState("");

  const [selectedRole, setSelectedRole] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const allowSuggestions = useMemo(() => collectAllowSuggestions(roles), [roles]);

  const loadRoles = useCallback(async (name = appliedName) => {
    setLoading(true);
    setError("");
    try {
      const payload = isSystem
        ? await (api?.getAllSystemRoles || systemRoleService.getAllSystemRoles)()
        : await tenantRoleService.getAllTenantRoles(requestTenantId, accessToken);
      const normalized = normalizeRoleList(payload).filter((role) =>
        matchesRoleSearch(role, { name })
      );
      setRoles(normalized);
    } catch (err) {
      const message = getApiErrorMessage(err);
      setRoles([]);
      setError(message);
      dispatch(appActions.apiErrorMessage(message));
    } finally {
      setLoading(false);
    }
  }, [accessToken, api, appliedName, dispatch, isSystem, requestTenantId]);

  useEffect(() => {
    loadRoles(appliedName);
  }, [appliedName, loadRoles]);

  const fetchTenantRoleDetail = useCallback(
    async (role) => {
      const roleId = String(role?.roleId || role?.id || "");
      const name = String(role?.name || "");
      const canUseId =
        /^[a-f0-9]{24}$/i.test(roleId) ||
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          roleId
        );

      if (canUseId && roleId !== name) {
        try {
          return await tenantRoleService.getTenantRoleById(
            roleId,
            requestTenantId,
            accessToken
          );
        } catch {
          // Fall back to get-by-name.
        }
      }

      if (!name) return null;
      return tenantRoleService.getTenantRoleByName(
        name,
        requestTenantId,
        accessToken
      );
    },
    [accessToken, requestTenantId]
  );

  const openDetails = useCallback(async (row) => {
    const current = normalizeRole(row);
    setSelectedRole(current);
    setDetailsOpen(true);

    try {
      const detail = isSystem
        ? current.name
          ? await (api?.getSystemRoleByName ||
              systemRoleService.getSystemRoleByName)(current.name)
          : null
        : await fetchTenantRoleDetail(current);
      if (detail) {
        setSelectedRole(normalizeRole({ ...current, ...(detail || {}) }));
      }
    } catch {
      // Keep list data if get-by-id/name is unavailable.
    }
  }, [api, fetchTenantRoleDetail, isSystem]);

  const openCreate = () => {
    setFormMode("create");
    setSelectedRole(null);
    setFormOpen(true);
  };

  const openEdit = useCallback((row) => {
    if (!row) return;
    setSelectedRole(normalizeRole(row));
    setFormMode("edit");
    setDetailsOpen(false);
    setFormOpen(true);
  }, []);

  const openDelete = useCallback((row) => {
    if (!row) return;
    setSelectedRole(normalizeRole(row));
    setDetailsOpen(false);
    setDeleteOpen(true);
  }, []);

  const resolveRoleId = useCallback(async (role) => {
    const currentId = String(role?.roleId || role?.id || "");
    if (/^[a-f0-9]{24}$/i.test(currentId)) return currentId;
    if (!role?.name) return currentId || "";

    if (isSystem) {
      if (!api?.getSystemRoleByName) return currentId || "";
      const detail = await api.getSystemRoleByName(role.name);
      const normalized = normalizeRole({ ...role, ...(detail || {}) });
      const detailId = String(normalized.roleId || "");
      return /^[a-f0-9]{24}$/i.test(detailId) ? detailId : detailId || currentId;
    }

    try {
      const detail = await fetchTenantRoleDetail(role);
      const normalized = normalizeRole({ ...role, ...(detail || {}) });
      const detailId = String(normalized.roleId || "");
      return /^[a-f0-9]{24}$/i.test(detailId) ? detailId : detailId || currentId;
    } catch {
      return currentId || "";
    }
  }, [accessToken, api, fetchTenantRoleDetail, isSystem, requestTenantId]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (formMode === "create") {
        if (isSystem) {
          await api.createSystemRole(payload);
        } else {
          await tenantRoleService.createTenantRole(
            payload,
            requestTenantId,
            accessToken
          );
        }
        dispatch(appActions.apiSuccessMessage("Role created successfully."));
      } else {
        const roleId = await resolveRoleId(selectedRole);
        if (!roleId) return;
        if (isSystem) {
          await api.updateSystemRole(roleId, payload);
        } else {
          await tenantRoleService.updateTenantRole(
            roleId,
            payload,
            requestTenantId,
            accessToken
          );
        }
        dispatch(appActions.apiSuccessMessage("Role updated successfully."));
      }
      setFormOpen(false);
      await loadRoles(appliedName);
    } catch (err) {
      throw new Error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      const roleId = await resolveRoleId(selectedRole);
      if (!roleId) return;
      if (isSystem) {
        await api.deleteSystemRole(roleId);
      } else {
        await tenantRoleService.deleteTenantRole(
          roleId,
          requestTenantId,
          accessToken
        );
      }
      dispatch(appActions.apiSuccessMessage("Role deleted successfully."));
      setDeleteOpen(false);
      setDetailsOpen(false);
      await loadRoles(appliedName);
    } catch (err) {
      dispatch(appActions.apiErrorMessage(getApiErrorMessage(err)));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Role Name",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => <TruncatedText value={params.value} />,
      },
      {
        field: "allow",
        headerName: "Permissions",
        flex: 1.6,
        minWidth: 220,
        sortable: false,
        valueGetter: (value, row) =>
          Array.isArray(row?.allow) ? row.allow.join(", ") : "",
        renderCell: (params) => (
          <ChipList items={params.row.allow} maxVisible={2} />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 168,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <ActionIconButton
              title="View Details"
              icon={<VisibilityOutlinedIcon fontSize="small" />}
              onClick={() => openDetails(params.row)}
            />
            <ActionIconButton
              title="Edit Role"
              icon={<EditOutlinedIcon fontSize="small" />}
              onClick={() => openEdit(params.row)}
            />
            <ActionIconButton
              title="Delete Role"
              icon={<DeleteOutlineOutlinedIcon fontSize="small" />}
              onClick={() => openDelete(params.row)}
            />
          </Stack>
        ),
      },
    ],
    [openDelete, openDetails, openEdit]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1.5}
        sx={{ mb: 1 }}
      >
        <PageHeader
          title="Roles"
          subtitle={
            isSystem
              ? "Manage system roles and their permissions."
              : "Manage tenant roles and their permissions."
          }
          sx={{ mb: 0 }}
        />
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<AddOutlinedIcon />}
            onClick={openCreate}
          >
            Add Role
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshOutlinedIcon />}
            onClick={() => loadRoles(appliedName)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      <SearchFilterBar
        fields={[
          {
            key: "name",
            label: "Role Name",
            value: nameFilter,
            onChange: (e) => setNameFilter(e.target.value),
            width: 280,
          },
        ]}
        onSearch={() => setAppliedName(nameFilter.trim())}
        onClear={() => {
          setNameFilter("");
          setAppliedName("");
        }}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          minHeight: 360,
        }}
      >
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading && !roles.length ? (
          <PageLoader text="Loading roles..." />
        ) : !loading && !roles.length ? (
          <EmptyState
            title="No roles found"
            description="No roles found for the current filters."
          />
        ) : (
          <DataTable
            rows={roles}
            columns={columns}
            pageSize={20}
            showPagination
            loading={loading}
            autoHeight
            getRowId={(row) => row.roleId || row.name}
            paginationMode="client"
          />
        )}
      </Paper>

      <RoleDetailsDrawer
        open={detailsOpen}
        role={selectedRole}
        onClose={() => setDetailsOpen(false)}
        onEdit={() => openEdit(selectedRole)}
        onDelete={() => openDelete(selectedRole)}
      />

      <RoleFormDialog
        open={formOpen}
        mode={formMode}
        role={selectedRole}
        allowSuggestions={allowSuggestions}
        loading={saving}
        tenantMode={!isSystem}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={saving ? undefined : () => setDeleteOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete role{" "}
            <strong>{selectedRole?.name || selectedRole?.roleId}</strong>? This
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
